import { bind } from 'bind-event-listener';
// Ignored via go/ees005
// eslint-disable-next-line import/no-namespace
import * as clipboard from 'clipboard-polyfill';

import { getDocument } from '@atlaskit/browser-apis';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, NodeSelection } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';

import type {
	AnalyticsEventPayload,
	INPUT_METHOD,
	SelectCellAEP,
	SelectNodeAEP,
	SelectRangeAEP,
} from '../analytics';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE } from '../analytics';
import {
	getAllSelectionAnalyticsPayload,
	getCellSelectionAnalyticsPayload,
	getNodeSelectionAnalyticsPayload,
	getRangeSelectionAnalyticsPayload,
} from '../selection';

const isClipboardApiSupported = () =>
	!!navigator.clipboard && typeof navigator.clipboard.writeText === 'function';

const isIEClipboardApiSupported = () =>
	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(window as any).clipboardData && typeof (window as any).clipboardData.setData === 'function';

const isExtensionNode = (node?: string) => {
	if (
		node === 'extension' ||
		node === 'bodiedExtension' ||
		node === 'inlineExtension' ||
		node === 'multiBodiedExtension'
	) {
		return true;
	}
	return false;
};

export const copyToClipboard = async (textToCopy: string): Promise<void> => {
	if (isClipboardApiSupported()) {
		try {
			await navigator.clipboard.writeText(textToCopy);
		} catch (error) {
			throw new Error('Clipboard api is not supported');
		}
	} else if (isIEClipboardApiSupported()) {
		try {
			// Ignored via go/ees005
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			await (window as any).clipboardData.setData('text', textToCopy);
		} catch (error) {
			throw new Error('IE clipboard api is not supported');
		}
	} else {
		throw new Error('Clipboard api is not supported');
	}
};

// EDF-2436: the copy helpers below are called fire-and-forget from click handlers, so a
// late rejection (eg. the polyfill's DOM fallback running after the document is gone)
// becomes an unhandled rejection and takes down the whole process. Attaching a no-op
// handler marks it handled; callers that await still receive the error.
const markRejectionHandled = <T>(promise: Promise<T>): Promise<T> => {
	promise.catch(() => {});
	return promise;
};

const writeHTMLToClipboard = async (
	elementToCopy: HTMLElement,
	plainTextToCopy?: string,
): Promise<void> => {
	if (isClipboardApiSupported() && typeof ClipboardItem !== 'undefined') {
		try {
			const data = new ClipboardItem({
				'text/plain': new Blob([plainTextToCopy || elementToCopy.innerText], {
					type: 'text/plain',
				}),
				'text/html': new Blob([elementToCopy.innerHTML], {
					type: 'text/html',
				}),
			});
			await navigator.clipboard.write([data]);
		} catch (error) {
			throw new Error('Clipboard api is not supported');
		}
	} else if (!!document && typeof document === 'object') {
		try {
			// ED-17083 extension copy seems have issue with ClipboardItem API
			// Hence of use of this polyfill
			// Awaited so the polyfill's async failures land in the catch below.
			await copyHTMLToClipboardPolyfill(elementToCopy, plainTextToCopy);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		}
	}
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const copyHTMLToClipboard = (
	elementToCopy: HTMLElement,
	plainTextToCopy?: string,
): Promise<void> => markRejectionHandled(writeHTMLToClipboard(elementToCopy, plainTextToCopy));

const writeHTMLToClipboardPolyfill = async (
	elementToCopy: HTMLElement,
	plainTextToCopy?: string,
): Promise<void> => {
	const dt = new clipboard.ClipboardItem({
		'text/html': new Blob([elementToCopy.innerHTML], { type: 'text/html' }),
		'text/plain': new Blob([plainTextToCopy || elementToCopy.innerText], { type: 'text/plain' }),
	});
	await clipboard.write([dt]);
};

// At the time of development, Firefox doesn't support ClipboardItem API
// Hence of use of this polyfill
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const copyHTMLToClipboardPolyfill = (
	elementToCopy: HTMLElement,
	plainTextToCopy?: string,
): Promise<void> =>
	markRejectionHandled(writeHTMLToClipboardPolyfill(elementToCopy, plainTextToCopy));

// Safari can revoke transient user activation before asynchronous clipboard writes complete.
// Keep this helper synchronous so callers can invoke it directly from a click handler.
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const copyHTMLToClipboardSynchronously = (
	elementToCopy: HTMLElement,
	plainTextToCopy?: string,
): boolean => {
	const document = getDocument();
	if (!document) {
		return false;
	}

	let didWriteClipboardData = false;
	const unbind = bind(document, {
		type: 'copy',
		listener: (event: ClipboardEvent) => {
			if (!event.clipboardData) {
				return;
			}

			event.preventDefault();
			event.clipboardData.setData('text/html', elementToCopy.innerHTML);
			event.clipboardData.setData('text/plain', plainTextToCopy || elementToCopy.innerText);
			didWriteClipboardData = true;
		},
	});

	try {
		return document.execCommand('copy') && didWriteClipboardData;
	} catch {
		return false;
	} finally {
		unbind();
	}
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getAnalyticsPayload = (
	state: EditorState,
	action: ACTION.CUT | ACTION.COPIED,
): AnalyticsEventPayload | undefined => {
	const { selection, doc } = state;
	const selectionAnalyticsPayload =
		getNodeSelectionAnalyticsPayload(selection) ||
		getRangeSelectionAnalyticsPayload(selection, doc) ||
		getAllSelectionAnalyticsPayload(selection) ||
		getCellSelectionAnalyticsPayload(state);

	if (selectionAnalyticsPayload) {
		const { actionSubjectId: selectionActionSubjectId } = selectionAnalyticsPayload;

		const node = (selectionAnalyticsPayload as SelectNodeAEP).attributes?.node;
		const content: string[] = [];
		let extensionType: string | undefined;
		let extensionKey: string | undefined;
		switch (selectionActionSubjectId) {
			case ACTION_SUBJECT_ID.NODE:
				if (node) {
					content.push(node);
					if (isExtensionNode(node)) {
						extensionType = (selection as NodeSelection).node.attrs.extensionType;
						extensionKey = (selection as NodeSelection).node.attrs.extensionKey;
					}
				}
				break;
			case ACTION_SUBJECT_ID.RANGE:
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				content.push(...(selectionAnalyticsPayload as SelectRangeAEP).attributes!.nodes);
				break;
			case ACTION_SUBJECT_ID.ALL:
				content.push('all');
				break;
			case ACTION_SUBJECT_ID.CELL: {
				// Ignored via go/ees005
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const { selectedCells } = (selectionAnalyticsPayload as SelectCellAEP).attributes!;
				content.push(...Array(selectedCells).fill('tableCell'));
				break;
			}
		}

		if (isExtensionNode(node)) {
			return {
				eventType: EVENT_TYPE.TRACK,
				action,
				actionSubject: ACTION_SUBJECT.DOCUMENT,
				attributes: {
					content,
					extensionKey,
					extensionType,
				},
			};
		}

		return {
			eventType: EVENT_TYPE.TRACK,
			action,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			attributes: {
				content,
			},
		};
	}

	if (selection instanceof TextSelection && selection.$cursor) {
		return {
			eventType: EVENT_TYPE.TRACK,
			action,
			actionSubject: ACTION_SUBJECT.DOCUMENT,
			attributes: {
				content: ['caret'],
			},
		};
	}
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getNodeCopiedAnalyticsPayload = (
	node: PMNode,
	inputMethod?: INPUT_METHOD,
): AnalyticsEventPayload => {
	const nodeType = node.type.name;
	let extensionType: string | undefined;
	let extensionKey: string | undefined;

	if (isExtensionNode(nodeType)) {
		extensionType = node.attrs.extensionType;
		extensionKey = node.attrs.extensionKey;
	}

	const payload: AnalyticsEventPayload = {
		eventType: EVENT_TYPE.TRACK,
		action: ACTION.COPIED,
		actionSubject: ACTION_SUBJECT.DOCUMENT,
		attributes: {
			content: [nodeType],
			inputMethod,
			nodeType,
			...(extensionType ? { extensionType } : {}),
			...(extensionKey ? { extensionKey } : {}),
		},
	};

	return payload;
};
